/**
 * Why the visitor asked for the auth modal.
 *
 * The header's single "Sign in" item is genuinely ambiguous — it opens the modal
 * for both signing in and registering — but a "Start Free" or "Sign Up Free" CTA
 * is not. Someone who clicks one of those has already decided to register, and
 * landing them on the Sign In tab makes them find and click a second thing before
 * they can do the one thing they asked for. `openSignIn` therefore takes an
 * intent, and the modal opens on the matching tab.
 */
export type AuthIntent = 'signIn' | 'signUp';

/** Tab ids of the main view's `Tabs`. Kept in step with `LocalingosAuthenticator`. */
export type AuthTabId = 'signin' | 'signup';

/**
 * What a call site that says nothing gets. Sign In, because that is what the
 * modal has always opened on and because a returning customer is the common case
 * for the header entry point.
 */
export const DEFAULT_AUTH_INTENT: AuthIntent = 'signIn';

const AUTH_INTENTS: readonly string[] = ['signIn', 'signUp'];

export function isAuthIntent(value: unknown): value is AuthIntent {
    return typeof value === 'string' && AUTH_INTENTS.includes(value);
}

/**
 * Coerces whatever a caller handed `openSignIn` into an intent.
 *
 * Several call sites are written `onClick={openSignIn}`, which means the first
 * argument they pass is a React MouseEvent, not an intent. Those callers want the
 * default, so anything that is not an AuthIntent resolves to it rather than
 * throwing or opening the wrong tab.
 */
export function resolveAuthIntent(value: unknown): AuthIntent {
    return isAuthIntent(value) ? value : DEFAULT_AUTH_INTENT;
}

/** The tab the modal should open on for a given intent. */
export function initialTabForIntent(intent: AuthIntent | undefined): AuthTabId {
    return intent === 'signUp' ? 'signup' : 'signin';
}
